import {
  HAND_SIZE,
  isAce,
  isClub,
  isJack,
  sideOf,
  TABLE_SIZE,
  TEN_OF_DIAMONDS,
  TWO_OF_CLUBS,
  type PasurDealResult,
  type PasurPlay,
  type PasurSideTally,
  type PasurView,
} from "@bing/engine-pasur";

/* ------------------------------------------------------------------ *
 * پاسور'S MOMENTS — what a new frame brings that the table must SHOW before it shows the frame
 * (P1 Task 5, W5–W7, W16). PURE: no React, no copy, no web-app import — so the engine's pacing test
 * (Task 6) can import the durations below and pin `postMoveDelayMs` against them, never a copy.
 *
 * Operator, 2026-10-10: پاسور must match حکم and بی‌دل — «the last card and the full collect
 * animation always shown» (table-play's 1fa3c258, `use-trick-hold.ts`). پاسور is not a trick game,
 * so its «move resolves» moment is its own (the reference's D11): a CAPTURE — the played card meets
 * the set it takes, holds, and the set slides to the capturer's seat — and, at a deal's end, the
 * SCOOP — the remainder slides to the deal's last capturer. A frame that arrives while one is on
 * screen waits its turn; nothing a later frame says cuts one short (`use-capture-hold.ts`).
 * ------------------------------------------------------------------ */

/** How long a capture HOLDS — the played card's 400 ms flight onto its set (`CARD_ENTRY_MS`) and
 *  300 ms to read which cards it took — before the set starts sliding to the capturer's seat. A
 *  سور and its cancel are captures: the same hold (their banner is a transient notice that
 *  outlives the moment). */
export const PASUR_CAPTURE_HOLD_MS = 700;
/** The set's slide to the capturer's seat (`collectSweep`). */
export const PASUR_CAPTURE_COLLECT_MS = 400;
/** A capture on screen, from its first render to its release: nothing a newer frame says — a
 *  bot's play, a human's instant one, the deal's last card — shows before this has run. */
export const PASUR_CAPTURE_MS = PASUR_CAPTURE_HOLD_MS + PASUR_CAPTURE_COLLECT_MS;
/** A سور, or a سور that cancels one (D10): a capture, so exactly a capture's span. Its own name so
 *  Task 6's «the سور hold» pins against it by name. */
export const PASUR_SUR_MS = PASUR_CAPTURE_MS;
/** How long the remainder HOLDS at a deal's end — a trailed last card's 400 ms flight onto the pool
 *  and a beat to see what is left — before it slides to the deal's last capturer (W16). */
export const PASUR_SCOOP_HOLD_MS = 700;
/** The remainder's slide to the last capturer. */
export const PASUR_SCOOP_COLLECT_MS = 500;
/** The scoop on screen, from its first render to its release. */
export const PASUR_SCOOP_MS = PASUR_SCOOP_HOLD_MS + PASUR_SCOOP_COLLECT_MS;
/**
 * THE LONGEST DEAL END THE TABLE DRAWS before the next deal shows: the deal's last play captures
 * (its hold and collect), then the scoop. Task 6's deal-end beat must cover it AND Task 7's per-deal
 * breakdown card: `postMoveDelayMs` on a new deal's first frame ≥ PASUR_DEAL_END_MS + the card's
 * display constant + 150 ms (R6). A deal whose last play trails is PASUR_SCOOP_MS; one whose last
 * capture clears the table, PASUR_CAPTURE_MS.
 */
export const PASUR_DEAL_END_MS = PASUR_CAPTURE_MS + PASUR_SCOOP_MS;

/*
 * THE DEAL, FROM THE STOCK (Task 6, W14 — the operator's «always shown» holds for the deal too):
 * every round of a deal is a moment, seen whole. The opening deals 4 to each seat in turn from the
 * seat after the dealer, then 4 to the table; every refill round deals 4 to each seat. Each card flies
 * out of the STOCK (the felt's corner chip) — one after the other, a breath between one seat's four and
 * the next's. A buried Jack (D6) is laid with the table's four, lies there long enough to be seen, and
 * goes back INTO the stock — a plain return: where it sits in the pack is hidden (D6b) — before the
 * card that replaced it is laid in its place. The engine's pacing (`postMoveDelayMs`) holds the next
 * move, and the game-server's scripted beat the turn window, for all of it (R6).
 */
/** Between two cards leaving the stock, one after the other. */
export const PASUR_DEAL_CARD_MS = 60;
/** The breath between one seat's four (or the table's) and the next seat's. */
export const PASUR_DEAL_PACKET_MS = 120;
/** A dealt card's flight from the stock to its seat, your hand, or its place on the pool. */
export const PASUR_DEAL_FLIGHT_MS = 340;
/** A buried Jack lies face-up where it was laid this long — every seat sees it (D6) — … */
export const PASUR_BURY_SHOW_MS = 500;
/** … then goes back into the stock: a plain return, no place in the pack shown (D6b). */
export const PASUR_BURY_RETURN_MS = 340;
/**
 * THE PER-DEAL BREAKDOWN CARD'S DISPLAY (defined by Task 6, drawn by Task 7): after a deal's end (its
 * last play, then the scoop — `PASUR_DEAL_END_MS`, the card's START) and before the next deal is dealt.
 * The engine's deal-end beat holds the bots and the turn window for it: `postMoveDelayMs` on a new
 * deal's first frame ≥ that deal end + this + the next deal's opening + 150 ms (R6, pinned by
 * `packages/engines/pasur/test/pacing.test.ts`, which imports it).
 */
export const PASUR_BREAKDOWN_MS = 3000;

/**
 * THE END CARD WAITS FOR THE TABLE TO DRAIN (Task 5 fix round 1, F1; Task 7, note 2; Task 8a, residual 4
 * — the operator's «always shown»): the match's terminal frame brings that deal's end — at its longest,
 * its last play's capture and then the scoop — and then its breakdown card, and the end card comes up
 * PASUR_END_MARGIN_MS after the LAST of them has gone (`PasurTable`'s `onHolding`: `useEndReveal` in
 * `PasurGame`, the shared `OnlineGame`'s `endRevealHeld`) — never at a fixed time from the frame's
 * arrival: a frame that lands with plays this table has not drawn yet (a stalled delivery) shows them
 * first, and the deal's card would be cut. The shell's 1400 ms beat came up over the remainder's slide,
 * chimed before its cue and showed its points first.
 */
export const PASUR_END_MARGIN_MS = 150;
/** …so with nothing queued ahead of it, the end card is this long after the terminal frame lands: the
 *  deal end's whole length, its card, and the margin (the engine's pacing test pins it — R6). */
export const PASUR_END_REVEAL_MS = PASUR_DEAL_END_MS + PASUR_BREAKDOWN_MS + PASUR_END_MARGIN_MS;

/** When card `k` of a deal (0-based, in dealing order) leaves the stock. */
function leavesAt(k: number): number {
  return k * PASUR_DEAL_CARD_MS + Math.floor(k / HAND_SIZE) * PASUR_DEAL_PACKET_MS;
}

/** One burial on screen: the Jack's flight onto the table, its stay there, its return into the stock
 *  — the deal's plan here, its flights in `use-pasur-deal.ts` (one constant for both). */
export const BURIAL_MS = PASUR_DEAL_FLIGHT_MS + PASUR_BURY_SHOW_MS + PASUR_BURY_RETURN_MS;

/**
 * A deal on screen, from its first card leaving the stock to its last one landing: `packets` packets of
 * four (each seat's, then — an opening — the table's), then each burial, the replacement last.
 */
export function pasurDealMs(packets: number, burials: number): number {
  return leavesAt(packets * HAND_SIZE - 1) + PASUR_DEAL_FLIGHT_MS + burials * BURIAL_MS;
}

/** A capture that cleared the table made a سور, or cancelled one of the other side's (D10). */
export interface SurMark {
  readonly kind: "scored" | "cancelled";
  /**
   * A cancel: the seat whose standing سور it neutralised — the newest one its side made (LIFO over
   * the deal's log; the engine keeps a count per side, so «which one» is the table's convention).
   * Null for a سور that scored, and for a cancel whose victim the log does not hold (a position
   * built without its history — the table then names the side).
   */
  readonly victim: number | null;
}

interface MomentBase {
  /** Unique per moment and stable: `deal:index` for a capture, `deal:final` / `deal:scoop` at a
   *  deal's end. */
  readonly key: string;
  /** The play the moment is about — the capture; at a deal's end, the deal's last play. */
  readonly play: PasurPlay;
  /** The table as it stood just BEFORE `play`: what the hand, the HUD and every number show until
   *  the moment is over (spoiler discipline, Part B §4.12). */
  readonly prev: PasurView;
}

/** The played card meets its set on the pool, holds, and the set slides to the capturer's seat. */
export interface CaptureMoment extends MomentBase {
  readonly kind: "capture";
  /** The pool the play met, in its order — the captured set among it. */
  readonly before: readonly string[];
  readonly sur: SurMark | null;
  /** Each side's standing سورs once this play counts — shown from the moment's reveal. */
  readonly sursAfter: readonly number[];
}

/** The deal's remainder slides to its last capturer (D14, W16). */
export interface ScoopMoment extends MomentBase {
  readonly kind: "scoop";
  /** Who takes it — the deal's last capturer; null when nobody captured all deal (it is nobody's:
   *  the cards fade where they lie). */
  readonly seat: number | null;
  /** The remainder, in the pool's order — a trailed last card last (it lands first). */
  readonly cards: readonly string[];
}

/** A round of the deal, out of the stock (Task 6, W14): the opening, or a refill. */
export interface DealMoment {
  readonly kind: "deal";
  /** `deal:deal:round` — the opening is round 0. */
  readonly key: string;
  readonly round: "opening" | "refill";
  /** The frame the deal ends on — its hands, its pool, its stock: what the table draws while it deals
   *  (with the stock as it stood before the deal, counting down as the cards leave it). */
  readonly view: PasurView;
}

/**
 * THE DEAL'S BREAKDOWN CARD (Task 7, W15): after the deal's end — its last play, then the scoop — and
 * before the next deal is dealt; at the match's end, before the end card. Every number it shows comes
 * from the deal's result (`lastDeal`, public — never `GameResult`, which carries no `detail`).
 */
export interface BreakdownMoment {
  readonly kind: "breakdown";
  /** `deal:breakdown`. */
  readonly key: string;
  /** The finished deal's result: its tallies, its points, the totals after it. */
  readonly deal: PasurDealResult;
  /** The table as the deal's last play found it — its deal, its dealer, its seats. */
  readonly prev: PasurView;
}

export type Moment = CaptureMoment | ScoopMoment | DealMoment | BreakdownMoment;

/**
 * A moment a BACKLOG may collapse (fix round 1, F2a): a plain capture — no سور, no cancel, not a
 * deal's last play — and a round of the deal (Task 6): a stalled delivery that brings a deal behind
 * newer plays never replays a stale one. A سور, its cancel and a deal's end (its last capture, then
 * the scoop) are always shown, however many frames a stalled delivery brings at once: they carry a
 * banner, a cue, a count or a deal's points, and the deal's last card is the operator's «the last
 * card … always shown». So is the deal's breakdown card (Task 7): never collapsed.
 */
export function collapsible(m: Moment): boolean {
  if (m.kind === "deal") return true;
  return m.kind === "capture" && m.sur === null && !m.key.endsWith(":final");
}

/** A moment's two spans: its hold (the reveal comes at its end) and its slide. A deal's whole span is
 *  its hold — it has no slide. */
export function momentTiming(m: Moment): { readonly hold: number; readonly collect: number } {
  if (m.kind === "deal") return { hold: dealPlan(m).ms, collect: 0 };
  if (m.kind === "breakdown") return { hold: PASUR_BREAKDOWN_MS, collect: 0 };
  return m.kind === "capture"
    ? { hold: PASUR_CAPTURE_HOLD_MS, collect: PASUR_CAPTURE_COLLECT_MS }
    : { hold: PASUR_SCOOP_HOLD_MS, collect: PASUR_SCOOP_COLLECT_MS };
}

/** When a card leaves the stock (ms from the deal's start); it lands `PASUR_DEAL_FLIGHT_MS` later. */
export interface DealDeparture {
  readonly card: string;
  readonly at: number;
}

/** The deal, card by card — every time from the moment's start. */
export interface DealPlan {
  /** The whole deal on screen: the moment's hold. */
  readonly ms: number;
  /** Each seat's four, in dealing order (the seat after the dealer first, the dealer last): when its
   *  first card leaves the stock; card `j` of it leaves `j × PASUR_DEAL_CARD_MS` later. */
  readonly seats: readonly { readonly seat: number; readonly at: number }[];
  /** The opening's table, in the table's order (its places on the pool): when each card leaves. */
  readonly pool: readonly DealDeparture[];
  /** The opening's buried Jacks, in order (D6b: a Jack may come back as the next replacement): each
   *  leaves the stock at `at`, lands, lies `PASUR_BURY_SHOW_MS`, and goes back into it. */
  readonly buried: readonly DealDeparture[];
  /** The pool place every burial happens in — the table's last (the view says which Jacks were
   *  buried, never where they lay: the table lays them in its last place) — or null. */
  readonly burySlot: number | null;
  /** The stock's count as the deal starts … */
  readonly before: number;
  /** … and as it moves: −1 as a card leaves, +1 as a buried Jack is back in (never where). */
  readonly stock: readonly { readonly at: number; readonly delta: 1 | -1 }[];
}

/** The deal `m` card by card (pure): the seats' fours from the seat after the dealer, then — an
 *  opening — the table's four, its last place taking each buried Jack in turn and then its card. */
export function dealPlan(m: DealMoment): DealPlan {
  const v = m.view;
  const S = v.seats;
  const seats = Array.from({ length: S }, (_u, i) => ({
    seat: (v.dealer + 1 + i) % S,
    at: leavesAt(i * HAND_SIZE),
  }));
  const stock: { at: number; delta: 1 | -1 }[] = [];
  for (let k = 0; k < S * HAND_SIZE; k++) stock.push({ at: leavesAt(k), delta: -1 });
  if (m.round === "refill") {
    return {
      ms: pasurDealMs(S, 0),
      seats,
      pool: [],
      buried: [],
      burySlot: null,
      before: v.deckCount + S * HAND_SIZE,
      stock,
    };
  }
  const base = S * HAND_SIZE;
  const burySlot = v.buried.length > 0 ? TABLE_SIZE - 1 : null;
  const chainAt = leavesAt(base + TABLE_SIZE - 1);
  const buried = v.buried.map((card, j) => ({ card, at: chainAt + j * BURIAL_MS }));
  const pool = v.table.map((card, i) => ({
    card,
    at: i === burySlot ? chainAt + buried.length * BURIAL_MS : leavesAt(base + i),
  }));
  for (const p of pool) stock.push({ at: p.at, delta: -1 });
  for (const b of buried) {
    stock.push({ at: b.at, delta: -1 });
    stock.push({ at: b.at + BURIAL_MS, delta: 1 });
  }
  stock.sort((a, b) => a.at - b.at || b.delta - a.delta); // a Jack is back in before the next leaves
  return {
    ms: pasurDealMs(S + 1, buried.length),
    seats,
    pool,
    buried,
    burySlot,
    before: v.deckCount + base + v.table.length,
    stock,
  };
}

/** A round of the deal, as the frame it ends on (`view`) shows it. */
function dealMoment(view: PasurView, round: DealMoment["round"]): DealMoment {
  const r = Math.floor(view.playLog.length / (HAND_SIZE * view.seats));
  return { kind: "deal", key: `${view.dealNumber}:deal:${r}`, round, view };
}

/**
 * THE MATCH'S FIRST FRAME, AT MOUNT, IS ITS OPENING DEAL — the one frame a mount does not adopt in
 * silence: deal 0, nothing played, no deal behind it. Any other frame a table mounts on — a later
 * deal's first frame included — may be a RESUME or a rejoin, and replays nothing (no deal, no
 * shuffle). A solo game never persists the match's first frame (it is a beat — `isOpeningCeremony`
 * in the solo room), so a resume never lands on it.
 */
export function openingDealAt(view: PasurView): DealMoment | null {
  return view.phase === "playing" &&
    view.dealNumber === 0 &&
    view.lastDeal === null &&
    view.playLog.length === 0
    ? dealMoment(view, "opening")
    : null;
}

/** Each side's standing سورs once `play` counts (the engine's own arithmetic, D10). */
export function sursAfter(surs: readonly number[], play: PasurPlay): number[] {
  if (play.sur === undefined) return [...surs];
  const mine = sideOf(play.seat);
  return surs.map((n, side) =>
    play.sur === "scored" ? (side === mine ? n + 1 : n) : side !== mine ? Math.max(0, n - 1) : n,
  );
}

/**
 * WHOSE سور A CANCEL NEUTRALISED — the newest standing one the other side made before it (LIFO over
 * the deal's log, which holds every سور of the deal: they reset per deal). Null when `log[index]`
 * is no cancel, or the log does not hold a standing سور for that side.
 */
export function surVictim(log: readonly PasurPlay[], index: number): number | null {
  const standing: number[][] = [[], []];
  for (let k = 0; k <= index && k < log.length; k++) {
    const p = log[k]!;
    if (p.sur === undefined) continue;
    const side = sideOf(p.seat);
    if (p.sur === "scored") standing[side]?.push(p.seat);
    else {
      const victim = standing[1 - side]?.pop() ?? null;
      if (k === index) return victim;
    }
  }
  return null;
}

function surMarkOf(log: readonly PasurPlay[], index: number): SurMark | null {
  const p = log[index];
  if (p?.sur === "scored") return { kind: "scored", victim: null };
  if (p?.sur === "cancelled") return { kind: "cancelled", victim: surVictim(log, index) };
  return null;
}

/** A side's tally with `cards` taken (`sign` 1) or given back (-1) — the engine's own count
 *  (`tallyDeal`: the cards, the clubs, the aces, the Jacks, who holds the 2♣ and the 10♦). */
function tallyWith(t: PasurSideTally, cards: readonly string[], sign: 1 | -1): PasurSideTally {
  const n = (is: (c: string) => boolean): number => sign * cards.filter(is).length;
  const holds = (card: string, had: boolean): boolean => (cards.includes(card) ? sign === 1 : had);
  return {
    ...t,
    cards: t.cards + sign * cards.length,
    clubs: t.clubs + n(isClub),
    aces: t.aces + n(isAce),
    jacks: t.jacks + n(isJack),
    twoOfClubs: holds(TWO_OF_CLUBS, t.twoOfClubs),
    tenOfDiamonds: holds(TEN_OF_DIAMONDS, t.tenOfDiamonds),
  };
}
/** The tallies with `cards` moved onto (or off) `seat`'s side. */
const talliesWith = (
  tallies: readonly PasurSideTally[],
  seat: number,
  cards: readonly string[],
  sign: 1 | -1,
): PasurSideTally[] =>
  tallies.map((t, side) => (side === sideOf(seat) ? tallyWith(t, cards, sign) : t));

/** The view one play later, in what the table draws: the pool (a trail joins its end, a capture
 *  leaves the rest in order — the engine's own two operations), your hand, the log, the سورs — and
 *  the tallies, a capture's cards on its side (the engine's `captured` pile: the card and its set). */
function step(view: PasurView, play: PasurPlay, log: readonly PasurPlay[]): PasurView {
  const taken = new Set(play.capture);
  return {
    ...view,
    table:
      play.capture.length > 0
        ? view.table.filter((c) => !taken.has(c))
        : [...view.table, play.card],
    hand: play.seat === view.you ? view.hand.filter((c) => c !== play.card) : view.hand,
    playLog: log,
    surs: sursAfter(view.surs, play),
    tallies:
      play.capture.length > 0
        ? talliesWith(view.tallies, play.seat, [play.card, ...play.capture], 1)
        : view.tallies,
  };
}

/**
 * A REFILL THIS TABLE NEVER DREW (final review B-2, I-1): the view as the round starting at play
 * `i` met it — your hand that round's (every dealt card is played within its round: the cards you
 * play from `i` to the round's end in `log`, and, in the round `next` stands in, the cards `next`
 * still has), the stock one refill lower (each takes HAND_SIZE a seat), the final round once it is
 * out. Never the old round's hand and stock.
 */
function roundAt(cur: PasurView, i: number, log: readonly PasurPlay[], next: PasurView): PasurView {
  const end = i + HAND_SIZE * cur.seats;
  const played = log
    .slice(i, end)
    .filter((p) => p.seat === cur.you)
    .map((p) => p.card);
  const deckCount = Math.max(0, cur.deckCount - HAND_SIZE * cur.seats);
  return {
    ...cur,
    hand: [...played, ...(next.playLog.length < end ? next.hand : [])],
    deckCount,
    finalRound: deckCount === 0,
  };
}

function captureMoment(
  at: PasurView,
  play: PasurPlay,
  key: string,
  sur: SurMark | null,
): CaptureMoment {
  return {
    kind: "capture",
    key,
    play,
    prev: at,
    before: at.table,
    sur,
    sursAfter: sursAfter(at.surs, play),
  };
}

/** The plays `log[from..to)` met from `view`, in order: a capture moment per capture, and the view
 *  after them. Every capture is shown — a frame that brought two never cuts the first. A round's
 *  end among them is a refill this table never drew (`roundAt`, off `next` — the frame they came in). */
function walk(
  view: PasurView,
  log: readonly PasurPlay[],
  from: number,
  to: number,
  next: PasurView,
): { moments: CaptureMoment[]; view: PasurView } {
  const moments: CaptureMoment[] = [];
  let cur = view;
  for (let i = from; i < to; i++) {
    if (i > from && i % (HAND_SIZE * view.seats) === 0) cur = roundAt(cur, i, log, next);
    const play = log[i]!;
    if (play.capture.length > 0)
      moments.push(captureMoment(cur, play, `${view.dealNumber}:${i}`, surMarkOf(log, i)));
    cur = step(cur, play, log.slice(0, i + 1));
  }
  return { moments, view: cur };
}

const sameList = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((c, i) => c === b[i]);

/** The pool the deal's last play left, from the pool it met — or null when it could not have met it. */
function afterFinal(pool: readonly string[], play: PasurPlay): string[] | null {
  if (pool.includes(play.card)) return null;
  if (play.capture.length === 0) return [...pool, play.card];
  if (!play.capture.every((c) => pool.includes(c))) return null;
  const taken = new Set(play.capture);
  return pool.filter((c) => !taken.has(c));
}

/** `cards` in `order`'s order where it has them, the rest after — a pool rebuilt from the deal's
 *  result keeps the places the table last drew. */
function inOrder(cards: readonly string[], order: readonly string[]): string[] {
  const set = new Set(cards);
  return [...order.filter((c) => set.has(c)), ...cards.filter((c) => !order.includes(c))];
}

/**
 * THE DEAL'S END: its last play (a capture holds and collects; a trail lands with the scoop), the
 * scoop of what is left, and the deal's breakdown card (Task 7). From `at` — the table as this client last drew it — when it is the
 * frame before the last play (no stock, one card in all the hands) and the last play met exactly
 * its pool and left exactly the result's remainder (the usual case: one frame per play); otherwise
 * plays before the last never reached this table, and the pool the last play met is rebuilt from
 * the deal's result (its set and its remainder), the سورs from its tallies.
 */
function dealEnd(at: PasurView, result: PasurDealResult): Moment[] {
  const play = result.finalPlay;
  const left = afterFinal(at.table, play);
  // `at` is the frame before the last play only when no stock is left and ONE card is held in all
  // the hands — plays this table missed can leave the pool as it was (a trail re-captured) and the
  // hands not (final review, M-1)
  const exact =
    at.deckCount === 0 &&
    at.handCounts.reduce((n, c) => n + c, 0) === 1 &&
    left !== null &&
    (result.scoop === null
      ? play.capture.length === 0 || left.length === 0
      : sameList(left, result.scoop.cards));
  const met = exact
    ? at.table
    : inOrder(
        play.capture.length > 0
          ? [...(result.scoop?.cards ?? []), ...play.capture]
          : (result.scoop?.cards ?? []).filter((c) => c !== play.card),
        at.table,
      );
  // …and, rebuilt, what else the plays this table never drew changed (final review B-2, I-1): the
  // deal's last play is the final round's (no stock, D8), every hand is empty but its player's last
  // card, and the tallies are the deal's less that play's capture and the scoop
  const scooped = result.scoop;
  const tallies = talliesWith(
    play.capture.length > 0
      ? talliesWith(result.tallies, play.seat, [play.card, ...play.capture], -1)
      : result.tallies,
    scooped?.seat ?? play.seat,
    scooped?.cards ?? [],
    -1,
  );
  const from: PasurView = exact
    ? at
    : {
        ...at,
        table: met,
        surs: result.tallies.map((t) => t.surs),
        hand: play.seat === at.you ? [play.card] : [],
        handCounts: at.handCounts.map((_n, seat) => (seat === play.seat ? 1 : 0)),
        deckCount: 0,
        finalRound: true,
        tallies,
      };
  const deal = at.dealNumber;
  const moments: Moment[] = [];
  // the final round makes no سور (D8): the deal's last capture is a plain one
  if (play.capture.length > 0) moments.push(captureMoment(from, play, `${deal}:final`, null));
  const remainder = result.scoop?.cards ?? (play.capture.length === 0 ? [...met, play.card] : null);
  if (remainder !== null && remainder.length > 0)
    moments.push({
      kind: "scoop",
      key: `${deal}:scoop`,
      play,
      prev: from,
      seat: result.scoop?.seat ?? null,
      cards: remainder,
    });
  // …and the card: the deal's numbers, once its last card and the remainder have landed (Task 7)
  moments.push({ kind: "breakdown", key: `${deal}:breakdown`, deal: result, prev: from });
  return moments;
}

/** Has the table moved on — a play, a deal, a seat? (Frames that differ only as objects do not.) */
export function frameMoved(a: PasurView, b: PasurView): boolean {
  return (
    a.you !== b.you ||
    a.seats !== b.seats ||
    a.dealNumber !== b.dealNumber ||
    a.playLog.length !== b.playLog.length ||
    (a.lastDeal?.dealNumber ?? -1) !== (b.lastDeal?.dealNumber ?? -1)
  );
}

/**
 * THE MOMENTS `next` BRINGS over `prev` — the frame the table last accounted for — in the order they
 * happened; `null` when `next` is not a continuation of `prev` (another seat, a new match, deals
 * skipped): the table then adopts it as it stands, replaying nothing — as a mount does.
 *
 *   - within a deal: one capture moment per capture among the new plays, each with the pool it met
 *     — and, when the frame ENDS on a refill, the next round's deal (Task 6);
 *   - the frame that ends a deal — a new deal's first frame whose `lastDeal` is the deal `prev` was
 *     in, or the match's terminal frame (its log still the deal's): the plays before the last as
 *     above, then the deal's end (`dealEnd`), then — a new deal — its opening deal (Task 6); a new
 *     deal's own plays in the same frame are adopted, and so is its deal.
 *   A deal's end closes on its breakdown card (Task 7): before the next deal's opening, and at the
 *   match's terminal frame before the end card.
 */
export function momentsBetween(prev: PasurView, next: PasurView): readonly Moment[] | null {
  if (prev.you !== next.you || prev.seats !== next.seats) return null;
  const deal = prev.dealNumber;
  const result = next.lastDeal;
  const ended = result !== null && result.dealNumber === deal && prev.lastDeal?.dealNumber !== deal;
  if (!ended) {
    if (next.dealNumber !== deal || next.playLog.length < prev.playLog.length) return null;
    const { moments } = walk(prev, next.playLog, prev.playLog.length, next.playLog.length, next);
    // a frame whose last event is a refill: the next round, dealt from the stock (Task 6)
    return refilled(prev, next) ? [...moments, dealMoment(next, "refill")] : moments;
  }
  if (next.dealNumber === deal) {
    // the match's last frame: the deal's log is still on it, its last play the deal's last
    if (next.playLog.length <= prev.playLog.length) return null;
    const before = walk(prev, next.playLog, prev.playLog.length, next.playLog.length - 1, next);
    return [...before.moments, ...dealEnd(before.view, result)];
  }
  if (next.dealNumber !== deal + 1) return null;
  // …then the next deal's opening, dealt from the stock (Task 6) — unless its own plays are already
  // on the frame (a backlog): those are adopted, and so is the deal behind them
  const opening = next.playLog.length === 0 ? [dealMoment(next, "opening")] : [];
  return [...dealEnd(prev, result), ...opening];
}

/** `next` ENDS on a refill — the round's last play came in it, and nothing after the deal. */
function refilled(prev: PasurView, next: PasurView): boolean {
  const n = next.playLog.length;
  return next.phase === "playing" && n > prev.playLog.length && n % (HAND_SIZE * next.seats) === 0;
}

/**
 * WHAT THE TABLE DRAWS WHILE `m` IS ON SCREEN — the table as it stood before the moment's play
 * (its numbers, its stock, its dealer, its deal), with:
 *   the pool     the pool the play met (a capture), or the remainder (the scoop);
 *   the hand     yours less the card you played (a refill or a new deal shows only after it);
 *   the log      the play appended (its card flies in from its seat, its «play» cue sounds);
 *   the سورs     each side's count once the play counts — from the moment's reveal;
 *   the turn     the play's own seat: the felt draws that seat's move until the moment is over, so
 *                the strip and the seats' glow say so — never the live turn, which is a move nobody
 *                can make yet (the hand is disabled; at a deal's end it is the NEXT deal's first
 *                player over an empty hand — fix round 1, F2b).
 */
export function momentView(m: Moment, revealed: boolean): PasurView {
  // the breakdown card: the FINISHED deal — no pool, no hand, no stock, no final round; its points
  // joined to the totals (the scoop has landed), its tallies and سورs; the deal's last player
  if (m.kind === "breakdown") {
    const p = m.prev;
    return {
      ...p,
      turn: m.deal.finalPlay.seat,
      table: [],
      hand: [],
      handCounts: p.handCounts.map(() => 0),
      deckCount: 0,
      finalRound: false,
      playLog: [...p.playLog, m.deal.finalPlay],
      tallies: m.deal.tallies,
      surs: m.deal.tallies.map((t) => t.surs),
      totalScores: m.deal.totalsAfter,
      lastDeal: m.deal,
    };
  }
  // a deal: the frame it ends on, with the stock as it stood before it (the chip counts down as the
  // cards leave it) — and so no final round until the deal is over (the stock empties at its end)
  if (m.kind === "deal") return { ...m.view, deckCount: dealPlan(m).before, finalRound: false };
  const p = m.prev;
  return {
    ...p,
    turn: m.play.seat,
    table: m.kind === "capture" ? m.before : m.cards,
    hand: m.play.seat === p.you ? p.hand.filter((c) => c !== m.play.card) : p.hand,
    playLog: [...p.playLog, m.play],
    surs: m.kind === "capture" && revealed ? m.sursAfter : p.surs,
  };
}
